import {
  Avatar,
  Box,
  ButtonBase,
  Dialog,
  InputBase,
  LinearProgress,
  Skeleton,
  styled,
  Typography,
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { getPortalZoomScale } from 'components/ui/utilities/zoom';
import { getPrismScrollbarStyles } from 'theme';
import { SUPER_SEARCH_NAVIGATION_DELAY_MS } from './SuperSearch.constants';

interface ZoomStyleProps {
  isNavigating: boolean;
  zoomLevel: number;
}

interface ContextRowStyleProps {
  isDimmed: boolean;
}

interface ResultButtonStyleProps {
  isSelected: boolean;
}

interface ResultSubtitleStyleProps {
  isSelected: boolean;
}

export type SuperSearchTone =
  | 'company'
  | 'companyAction'
  | 'companyPage'
  | 'document'
  | 'firm'
  | 'firmAction'
  | 'firmPage'
  | 'firmSettings'
  | 'measurementDate'
  | 'neutral'
  | 'version';

interface ToneStyleProps {
  tone: SuperSearchTone;
}

interface ResultSubtitleSegmentStyleProps extends ToneStyleProps {
  preserveWidth: boolean;
}

const SUPER_SEARCH_SCALE_VAR = '--super-search-portal-scale';
const shouldForwardDialogProp = (prop: PropertyKey) => prop !== 'isNavigating' && prop !== 'zoomLevel';
const shouldForwardContextRowProp = (prop: PropertyKey) => prop !== 'isDimmed';
const shouldForwardResultButtonProp = (prop: PropertyKey) => prop !== 'isSelected';
const shouldForwardSelectedProp = (prop: PropertyKey) => prop !== 'isSelected';
const shouldForwardToneProp = (prop: PropertyKey) => prop !== 'tone';
const shouldForwardSubtitleSegmentProp = (prop: PropertyKey) => shouldForwardToneProp(prop) && prop !== 'preserveWidth';
const getScaledPx = (value: number) => `calc(${value}px * var(${SUPER_SEARCH_SCALE_VAR}, 1))`;
const navigationOverlayEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
const navigationIconEnter = keyframes`
  from {
    opacity: 0;
    transform: scale(0.5);
  }

  70% {
    opacity: 1;
    transform: scale(1.08);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
`;
const navigationProgressFill = keyframes`
  from {
    transform: scaleX(0);
  }

  to {
    transform: scaleX(1);
  }
`;

const getToneColors = (tone: SuperSearchTone, theme: Theme) => {
  switch (tone) {
    case 'company':
      return {
        background: alpha(theme.palette.green[500], 0.08),
        foreground: theme.palette.green[600],
      };
    case 'companyAction':
      return {
        background: alpha(theme.palette.purple[500], 0.08),
        foreground: theme.palette.purple[600],
      };
    case 'companyPage':
      return {
        background: alpha(theme.palette.orange[500], 0.08),
        foreground: theme.palette.orange[600],
      };
    case 'document':
      return {
        background: theme.palette.navigation.superSearch.tone.document.background,
        foreground: theme.palette.navigation.superSearch.tone.document.foreground,
      };
    case 'firmAction':
      return {
        background: alpha(theme.palette.red[500], 0.08),
        foreground: theme.palette.red[600],
      };
    case 'firmPage':
      return {
        background: theme.palette.navigation.superSearch.tone.firmPage.background,
        foreground: theme.palette.navigation.superSearch.tone.firmPage.foreground,
      };
    case 'firmSettings':
      return {
        background: theme.palette.navigation.superSearch.tone.firmSettings.background,
        foreground: theme.palette.navigation.superSearch.tone.firmSettings.foreground,
      };
    case 'measurementDate':
      return {
        background: theme.palette.navigation.superSearch.tone.measurementDate.background,
        foreground: theme.palette.navigation.superSearch.tone.measurementDate.foreground,
      };
    case 'version':
      return {
        background: theme.palette.navigation.superSearch.tone.version.background,
        foreground: theme.palette.navigation.superSearch.tone.version.foreground,
      };
    case 'neutral':
      return {
        background: theme.palette.neutral[100],
        foreground: theme.palette.neutral[600],
      };
    case 'firm':
    default:
      return {
        background: alpha(theme.palette.brand[500], 0.08),
        foreground: theme.palette.brand[500],
      };
  }
};

export const StyledDialog = styled(Dialog, {
  shouldForwardProp: shouldForwardDialogProp,
})<ZoomStyleProps>(({ isNavigating, theme, zoomLevel }) => ({
  [SUPER_SEARCH_SCALE_VAR]: getPortalZoomScale(zoomLevel),
  '& .MuiBackdrop-root': {
    backgroundColor: alpha(theme.palette.neutral[900], 0.35),
    backdropFilter: 'blur(4px)',
  },
  '& .MuiDialog-container': {
    alignItems: 'flex-start',
    paddingTop: '12vh',
  },
  '& .MuiDialog-paper': {
    backgroundColor: theme.palette.common.white,
    borderRadius: getScaledPx(theme.shape.radius.m),
    boxShadow: `0 0 0 ${getScaledPx(1)} ${alpha(theme.palette.neutral[900], 0.07)}, 0 ${getScaledPx(20)} ${getScaledPx(60)} ${alpha(theme.palette.neutral[900], 0.13)}`,
    boxSizing: 'border-box',
    margin: `0 ${getScaledPx(16)}`,
    maxHeight: `min(${getScaledPx(460)}, calc(100vh - ${getScaledPx(96)}))`,
    maxWidth: `calc(100vw - ${getScaledPx(32)})`,
    minHeight: isNavigating ? getScaledPx(280) : 0,
    overflow: 'hidden',
    position: 'relative',
    width: getScaledPx(760),
  },
  '@media (max-width: 720px)': {
    '& .MuiDialog-container': {
      paddingTop: '8vh',
    },
    '& .MuiDialog-paper': {
      margin: `0 ${getScaledPx(8)}`,
      maxHeight: `calc(100vh - ${getScaledPx(48)})`,
      maxWidth: `calc(100vw - ${getScaledPx(16)})`,
      width: '100%',
    },
  },
  '@media (max-height: 700px)': {
    '& .MuiDialog-container': {
      paddingTop: getScaledPx(40),
    },
    '& .MuiDialog-paper': {
      maxHeight: `calc(100vh - ${getScaledPx(64)})`,
    },
  },
  '@media (prefers-reduced-motion: reduce)': {
    '& *, & *::before, & *::after': {
      animationDuration: '0.01ms !important',
      animationIterationCount: '1 !important',
      scrollBehavior: 'auto !important',
      transitionDuration: '0.01ms !important',
    },
  },
}));

export const LoadingProgress = styled(LinearProgress)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.brand[500], 0.12),
  height: getScaledPx(2),
  left: 0,
  position: 'absolute',
  right: 0,
  top: 0,
  zIndex: 2,
  '& .MuiLinearProgress-bar': {
    backgroundColor: theme.palette.brand[500],
  },
}));

export const SearchHeader = styled(Box)(({ theme }) => ({
  borderBottom: `${getScaledPx(1)} solid ${theme.palette.neutral[200]}`,
  padding: `0 ${getScaledPx(16)}`,
  transition: theme.transitions.create('box-shadow', { duration: 120 }),
  '&:focus-within': {
    boxShadow: `inset 0 -${getScaledPx(2)} 0 ${alpha(theme.palette.brand[500], 0.75)}`,
  },
  '@media (max-width: 720px)': {
    padding: `0 ${getScaledPx(12)}`,
  },
}));

export const ContextRow = styled(Box, {
  shouldForwardProp: shouldForwardContextRowProp,
})<ContextRowStyleProps>(({ isDimmed, theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flex: '0 1 auto',
  gap: getScaledPx(3),
  maxWidth: getScaledPx(360),
  minWidth: 0,
  opacity: isDimmed ? 0.38 : 1,
  overflow: 'hidden',
  transition: theme.transitions.create('opacity', { duration: 140 }),
  whiteSpace: 'nowrap',
  '@media (max-width: 720px)': {
    maxWidth: '42vw',
  },
}));

const getContextSegmentStyles = (theme: Theme, tone: SuperSearchTone) =>
  ({
    borderRadius: getScaledPx(theme.shape.radius.xs),
    color: getToneColors(tone, theme).foreground,
    display: 'block',
    flex: '0 1 auto',
    fontSize: getScaledPx(10.5),
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 1,
    maxWidth: getScaledPx(150),
    minWidth: 0,
    overflow: 'hidden',
    padding: `${getScaledPx(2)} ${getScaledPx(3)}`,
    textAlign: 'left',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }) as const;

export const ContextButton = styled(ButtonBase, {
  shouldForwardProp: shouldForwardToneProp,
})<ToneStyleProps>(({ theme, tone }) => ({
  ...getContextSegmentStyles(theme, tone),
  transition: theme.transitions.create('background-color', { duration: 140 }),
  '&:hover': {
    backgroundColor: theme.palette.neutral[100],
  },
  '&:focus-visible': {
    backgroundColor: theme.palette.neutral[100],
    boxShadow: `0 0 0 ${getScaledPx(2)} ${alpha(theme.palette.brand[500], 0.24)}`,
    outline: 'none',
  },
}));

export const ContextCurrent = styled(Box, {
  shouldForwardProp: shouldForwardToneProp,
})<ToneStyleProps>(({ theme, tone }) => getContextSegmentStyles(theme, tone));

export const ContextSeparator = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.neutral[500],
  display: 'flex',
  flex: '0 0 auto',
  '& svg': {
    fontSize: getScaledPx(11),
  },
}));

export const SearchInputRow = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.common.white,
  display: 'flex',
  gap: getScaledPx(12),
  minHeight: getScaledPx(46),
  padding: `${getScaledPx(14)} 0`,
  '& > svg': {
    color: theme.palette.neutral[500],
    flex: '0 0 auto',
    fontSize: getScaledPx(21),
  },
  '@media (max-width: 720px)': {
    gap: getScaledPx(8),
  },
}));

export const SearchTextField = styled(Box)({
  flex: '1 1 auto',
  minWidth: 0,
  position: 'relative',
});

export const SearchInput = styled(InputBase)(({ theme }) => ({
  color: theme.palette.neutral[900],
  display: 'flex',
  fontSize: getScaledPx(12),
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: getScaledPx(17),
  position: 'relative',
  width: '100%',
  zIndex: 1,
  '& input': {
    boxSizing: 'border-box',
    padding: 0,
  },
  '& input::placeholder': {
    color: theme.palette.neutral[500],
    opacity: 1,
  },
}));

export const GhostText = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.neutral[400],
  display: 'flex',
  fontSize: getScaledPx(12),
  height: '100%',
  left: 0,
  lineHeight: getScaledPx(17),
  overflow: 'hidden',
  pointerEvents: 'none',
  position: 'absolute',
  right: 0,
  top: 0,
  whiteSpace: 'pre',
}));

export const GhostPrefix = styled('span')({
  visibility: 'hidden',
});

export const ResultsRoot = styled(Box)(({ theme }) => ({
  flex: '1 1 auto',
  maxHeight: getScaledPx(380),
  minHeight: 0,
  overflowY: 'auto',
  padding: `${getScaledPx(6)} 0`,
  scrollPaddingBlock: getScaledPx(32),
  ...getPrismScrollbarStyles(theme, { width: getScaledPx(8) }),
}));

export const ResultsList = styled(Box)({
  minHeight: 0,
});

export const AssistiveText = styled('span')({
  border: 0,
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: 1,
});

export const SectionRoot = styled(Box)({
  '& + &': {
    marginTop: getScaledPx(4),
  },
});

export const SectionLabel = styled(Typography, {
  shouldForwardProp: shouldForwardToneProp,
})<ToneStyleProps>(({ theme, tone }) => ({
  alignItems: 'center',
  color: getToneColors(tone, theme).foreground,
  display: 'flex',
  fontSize: getScaledPx(9),
  fontWeight: theme.typography.fontWeightBold,
  gap: getScaledPx(8),
  letterSpacing: '0.1em',
  lineHeight: getScaledPx(13),
  padding: `${getScaledPx(8)} ${getScaledPx(16)} ${getScaledPx(4)}`,
  textTransform: 'uppercase',
  '&::after': {
    backgroundColor: theme.palette.neutral[200],
    content: '""',
    flex: '1 1 auto',
    height: getScaledPx(1),
  },
}));

export const ResultButton = styled(ButtonBase, {
  shouldForwardProp: shouldForwardResultButtonProp,
})<ResultButtonStyleProps>(({ isSelected, theme }) => ({
  alignItems: 'center',
  backgroundColor: isSelected ? alpha(theme.palette.brand[500], 0.05) : 'transparent',
  color: isSelected ? theme.palette.neutral[900] : theme.palette.neutral[700],
  display: 'flex',
  gap: getScaledPx(12),
  minHeight: getScaledPx(42),
  padding: `${getScaledPx(7)} ${getScaledPx(16)}`,
  position: 'relative',
  textAlign: 'left',
  transition: theme.transitions.create('background-color', { duration: 120 }),
  width: '100%',
  '&::before': {
    backgroundColor: theme.palette.brand[500],
    bottom: 0,
    content: '""',
    left: 0,
    opacity: isSelected ? 1 : 0,
    position: 'absolute',
    top: 0,
    width: getScaledPx(2),
  },
  '&:hover': {
    backgroundColor: isSelected ? alpha(theme.palette.brand[500], 0.05) : theme.palette.neutral[100],
  },
}));

export const ResultIcon = styled(Box, {
  shouldForwardProp: shouldForwardToneProp,
})<ToneStyleProps>(({ theme, tone }) => ({
  alignItems: 'center',
  backgroundColor: getToneColors(tone, theme).background,
  borderRadius: getScaledPx(theme.shape.radius.s),
  color: getToneColors(tone, theme).foreground,
  display: 'flex',
  flex: '0 0 auto',
  height: getScaledPx(28),
  justifyContent: 'center',
  width: getScaledPx(28),
  '& svg': {
    fontSize: getScaledPx(13),
  },
}));

export const ResultFirmAvatar = styled(Avatar)(({ theme }) => ({
  borderRadius: getScaledPx(theme.shape.radius.s),
  fontSize: getScaledPx(9),
  height: '100%',
  width: '100%',
}));

export const ResultCopy = styled(Box)({
  flex: '1 1 auto',
  minWidth: 0,
});

export const ResultLabel = styled(Typography)(({ theme }) => ({
  color: 'inherit',
  fontSize: getScaledPx(13),
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: getScaledPx(16),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const ResultSubtitle = styled(Typography, {
  shouldForwardProp: shouldForwardSelectedProp,
})<ResultSubtitleStyleProps>(({ isSelected, theme }) => ({
  alignItems: 'center',
  color: isSelected ? theme.palette.neutral[500] : alpha(theme.palette.neutral[500], 0.9),
  display: 'flex',
  fontSize: getScaledPx(11),
  gap: getScaledPx(4),
  lineHeight: getScaledPx(14),
  marginTop: getScaledPx(1),
  overflow: 'hidden',
  whiteSpace: 'nowrap',
}));

export const ResultSubtitleSegment = styled('span', {
  shouldForwardProp: shouldForwardSubtitleSegmentProp,
})<ResultSubtitleSegmentStyleProps>(({ preserveWidth, theme, tone }) => {
  const { 100: neutralBackground, 700: neutralForeground } = theme.palette.neutral;
  const toneColors = getToneColors(tone, theme);
  const foreground = tone === 'neutral' ? neutralForeground : toneColors.foreground;
  const isCompany = tone === 'company';
  const isFirm = tone === 'firm';
  const isMeasurementDate = tone === 'measurementDate';
  const isNeutral = tone === 'neutral';
  const isVersion = tone === 'version';
  let backgroundColor = alpha(foreground, 0.08);
  let flexShrink = 1;
  let maxWidth = getScaledPx(220);

  if (isNeutral) {
    backgroundColor = neutralBackground;
  } else if (isVersion) {
    backgroundColor = toneColors.background;
    flexShrink = 0;
    maxWidth = '50%';
  }

  if (preserveWidth) {
    flexShrink = 0;
    maxWidth = 'none';
  }

  if (isMeasurementDate) {
    flexShrink = 0;
    maxWidth = 'none';
  } else if (isCompany) {
    maxWidth = getScaledPx(300);
  } else if (isFirm) {
    flexShrink = 4;
  }

  return {
    backgroundColor,
    borderRadius: getScaledPx(theme.shape.radius.xs),
    color: foreground,
    display: 'block',
    flex: '0 1 auto',
    flexShrink,
    maxWidth,
    minWidth: 0,
    overflow: 'hidden',
    padding: `0 ${getScaledPx(4)}`,
    textOverflow: isMeasurementDate ? 'clip' : 'ellipsis',
    whiteSpace: 'nowrap',
    '&:first-of-type': {
      flexShrink: isMeasurementDate || preserveWidth || isVersion ? 0 : 0.25,
      maxWidth: isVersion ? '50%' : 'none',
    },
  };
});

export const ResultMeta = styled(Box)({
  alignItems: 'center',
  display: 'flex',
  flex: '0 0 auto',
  gap: getScaledPx(7),
});

export const ResultTypeBadge = styled(Box, {
  shouldForwardProp: shouldForwardToneProp,
})<ToneStyleProps>(({ theme, tone }) => ({
  backgroundColor: alpha(getToneColors(tone, theme).foreground, 0.07),
  borderRadius: getScaledPx(theme.shape.radius.xs),
  color: alpha(getToneColors(tone, theme).foreground, 0.65),
  fontSize: getScaledPx(9),
  fontWeight: theme.typography.fontWeightBold,
  letterSpacing: '0.05em',
  lineHeight: getScaledPx(13),
  padding: `${getScaledPx(2)} ${getScaledPx(6)}`,
  textTransform: 'uppercase',
}));

export const SearchLoadingState = styled(Box)(({ theme }) => ({
  color: theme.palette.neutral[500],
  padding: `${getScaledPx(10)} 0 ${getScaledPx(6)}`,
}));

export const SearchLoadingLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral[500],
  fontSize: getScaledPx(10),
  lineHeight: getScaledPx(14),
  padding: `0 ${getScaledPx(16)} ${getScaledPx(6)}`,
}));

export const SearchSkeletonRow = styled(Box)({
  alignItems: 'center',
  display: 'flex',
  gap: getScaledPx(12),
  minHeight: getScaledPx(42),
  padding: `${getScaledPx(7)} ${getScaledPx(16)}`,
});

export const SearchSkeletonIcon = styled(Skeleton)(({ theme }) => ({
  borderRadius: getScaledPx(theme.shape.radius.s),
  flex: '0 0 auto',
  height: getScaledPx(28),
  width: getScaledPx(28),
}));

export const SearchSkeletonCopy = styled(Box)({
  flex: '1 1 auto',
  minWidth: 0,
});

export const SearchSkeletonLine = styled(Skeleton)({
  height: getScaledPx(11),
  maxWidth: '100%',
});

export const SearchGuidance = styled(Box)(({ theme }) => ({
  borderBottom: `${getScaledPx(1)} solid ${theme.palette.neutral[100]}`,
  color: theme.palette.neutral[500],
  fontSize: getScaledPx(10),
  lineHeight: getScaledPx(14),
  padding: `${getScaledPx(7)} ${getScaledPx(16)}`,
}));

export const EmptyState = styled(Box)(() => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  minHeight: getScaledPx(150),
  padding: `${getScaledPx(22)} ${getScaledPx(24)} ${getScaledPx(18)}`,
  textAlign: 'center',
}));

export const EmptyIcon = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: alpha(theme.palette.brand[500], 0.07),
  border: `${getScaledPx(1)} solid ${alpha(theme.palette.brand[500], 0.12)}`,
  borderRadius: '50%',
  color: theme.palette.brand[500],
  display: 'flex',
  height: getScaledPx(38),
  justifyContent: 'center',
  width: getScaledPx(38),
  '& svg': {
    fontSize: getScaledPx(18),
  },
}));

export const EmptyTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral[900],
  fontSize: getScaledPx(13),
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: getScaledPx(18),
  marginTop: getScaledPx(10),
}));

export const EmptyDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral[500],
  fontSize: getScaledPx(11),
  lineHeight: getScaledPx(16),
  marginTop: getScaledPx(3),
  maxWidth: getScaledPx(420),
  overflowWrap: 'anywhere',
}));

export const EmptyActions = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: getScaledPx(7),
  justifyContent: 'center',
  marginTop: getScaledPx(12),
});

export const EmptyActionButton = styled(ButtonBase)(({ theme }) => ({
  backgroundColor: theme.palette.neutral[50],
  border: `${getScaledPx(1)} solid ${theme.palette.neutral[200]}`,
  borderRadius: getScaledPx(theme.shape.radius.xs),
  color: theme.palette.neutral[700],
  fontSize: getScaledPx(10),
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: getScaledPx(14),
  maxWidth: getScaledPx(260),
  overflow: 'hidden',
  padding: `${getScaledPx(5)} ${getScaledPx(9)}`,
  textOverflow: 'ellipsis',
  transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow'], { duration: 120 }),
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: theme.palette.neutral[100],
    borderColor: theme.palette.neutral[300],
  },
  '&:focus-visible': {
    borderColor: theme.palette.brand[500],
    boxShadow: `0 0 0 ${getScaledPx(2)} ${alpha(theme.palette.brand[500], 0.16)}`,
    outline: 'none',
  },
}));

export const ErrorState = styled(Box)(() => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  minHeight: getScaledPx(210),
  padding: `${getScaledPx(28)} ${getScaledPx(24)}`,
  textAlign: 'center',
}));

export const ErrorIcon = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: alpha(theme.palette.error.main, 0.08),
  border: `${getScaledPx(1)} solid ${alpha(theme.palette.error.main, 0.16)}`,
  borderRadius: '50%',
  color: theme.palette.error.main,
  display: 'flex',
  height: getScaledPx(42),
  justifyContent: 'center',
  width: getScaledPx(42),
  '& svg': {
    fontSize: getScaledPx(20),
  },
}));

export const ErrorTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral[900],
  fontSize: getScaledPx(14),
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: getScaledPx(19),
  marginTop: getScaledPx(12),
}));

export const ErrorDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral[500],
  fontSize: getScaledPx(11),
  lineHeight: getScaledPx(16),
  marginTop: getScaledPx(3),
  maxWidth: getScaledPx(320),
}));

export const ErrorRetryButton = styled(ButtonBase)(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.brand[500],
  borderRadius: getScaledPx(theme.shape.radius.xs),
  color: theme.palette.common.white,
  display: 'flex',
  fontSize: getScaledPx(11),
  fontWeight: theme.typography.fontWeightMedium,
  gap: getScaledPx(5),
  lineHeight: getScaledPx(15),
  marginTop: getScaledPx(14),
  padding: `${getScaledPx(6)} ${getScaledPx(10)}`,
  transition: theme.transitions.create(['background-color', 'box-shadow'], { duration: 120 }),
  '& svg': {
    fontSize: getScaledPx(15),
  },
  '&:hover': {
    backgroundColor: theme.palette.brand[600],
  },
  '&:focus-visible': {
    boxShadow: `0 0 0 ${getScaledPx(3)} ${alpha(theme.palette.brand[500], 0.22)}`,
    outline: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.neutral[300],
    color: theme.palette.neutral[500],
  },
}));

// Compact recovery UI when live search fails but stable cached results remain visible.
export const PartialErrorBanner = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: alpha(theme.palette.error.main, 0.04),
  borderBottom: `${getScaledPx(1)} solid ${alpha(theme.palette.error.main, 0.12)}`,
  display: 'flex',
  gap: getScaledPx(10),
  padding: `${getScaledPx(10)} ${getScaledPx(16)}`,
}));

export const PartialErrorIcon = styled(ErrorIcon)(() => ({
  flexShrink: 0,
  height: getScaledPx(28),
  width: getScaledPx(28),
  '& svg': {
    fontSize: getScaledPx(16),
  },
}));

export const PartialErrorCopy = styled(Box)({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  gap: getScaledPx(2),
  minWidth: 0,
});

export const PartialErrorTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral[900],
  fontSize: getScaledPx(12),
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: getScaledPx(16),
}));

export const PartialErrorDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral[500],
  fontSize: getScaledPx(11),
  lineHeight: getScaledPx(15),
}));

export const PartialErrorRetryButton = styled(ErrorRetryButton)(() => ({
  flexShrink: 0,
  marginTop: 0,
}));

export const NavigationOverlay = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  animation: `${navigationOverlayEnter} 160ms ease-out`,
  backgroundColor: alpha(theme.palette.common.white, 0.98),
  display: 'flex',
  flexDirection: 'column',
  inset: 0,
  justifyContent: 'center',
  padding: getScaledPx(24),
  position: 'absolute',
  textAlign: 'center',
  zIndex: 4,
}));

export const NavigationIcon = styled(Box, {
  shouldForwardProp: shouldForwardToneProp,
})<ToneStyleProps>(({ theme, tone }) => {
  const colors = getToneColors(tone, theme);

  return {
    alignItems: 'center',
    animation: `${navigationIconEnter} 320ms cubic-bezier(0.2, 1.4, 0.4, 1)`,
    backgroundColor: colors.background,
    border: `${getScaledPx(1)} solid ${alpha(colors.foreground, 0.2)}`,
    borderRadius: '50%',
    color: colors.foreground,
    display: 'flex',
    height: getScaledPx(56),
    justifyContent: 'center',
    width: getScaledPx(56),
    '& svg': {
      fontSize: getScaledPx(24),
    },
  };
});

export const NavigationTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral[900],
  fontSize: getScaledPx(13),
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: getScaledPx(17),
  marginTop: getScaledPx(16),
}));

export const NavigationTarget = styled(Typography, {
  shouldForwardProp: shouldForwardToneProp,
})<ToneStyleProps>(({ theme, tone }) => ({
  color: getToneColors(tone, theme).foreground,
  fontSize: getScaledPx(14),
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: getScaledPx(18),
  marginTop: getScaledPx(4),
  maxWidth: getScaledPx(420),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const NavigationContext = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral[500],
  fontSize: getScaledPx(11),
  lineHeight: getScaledPx(15),
  marginTop: getScaledPx(2),
  maxWidth: getScaledPx(420),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const NavigationProgress = styled(Box, {
  shouldForwardProp: shouldForwardToneProp,
})<ToneStyleProps>(({ theme, tone }) => {
  const color = getToneColors(tone, theme).foreground;

  return {
    backgroundColor: alpha(color, 0.12),
    borderRadius: getScaledPx(2),
    height: getScaledPx(3),
    marginTop: getScaledPx(16),
    overflow: 'hidden',
    position: 'relative',
    width: getScaledPx(128),
    '&::before, &::after': {
      animation: `${navigationProgressFill} ${SUPER_SEARCH_NAVIGATION_DELAY_MS}ms linear forwards`,
      backgroundColor: color,
      bottom: 0,
      content: '""',
      position: 'absolute',
      top: 0,
      width: '50%',
    },
    '&::before': {
      right: '50%',
      transformOrigin: 'right center',
    },
    '&::after': {
      left: '50%',
      transformOrigin: 'left center',
    },
  };
});

export const NavigationCancel = styled(ButtonBase)(({ theme }) => ({
  backgroundColor: 'transparent',
  borderRadius: getScaledPx(theme.shape.radius.xs),
  color: theme.palette.neutral[600],
  fontSize: getScaledPx(10),
  gap: getScaledPx(5),
  lineHeight: getScaledPx(14),
  marginTop: getScaledPx(12),
  padding: `${getScaledPx(3)} ${getScaledPx(4)}`,
  transition: theme.transitions.create(['background-color', 'color'], { duration: 120 }),
  '&:hover': {
    color: theme.palette.neutral[700],
  },
  '&:hover kbd, &:focus-visible kbd': {
    backgroundColor: alpha(theme.palette.brand[500], 0.12),
    borderColor: theme.palette.brand[500],
    boxShadow: `0 0 0 ${getScaledPx(2)} ${alpha(theme.palette.brand[500], 0.1)}`,
    color: theme.palette.brand[500],
  },
  '&:focus-visible': {
    color: theme.palette.neutral[700],
    outline: 'none',
  },
}));

export const Footer = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  borderTop: `${getScaledPx(1)} solid ${theme.palette.neutral[200]}`,
  color: theme.palette.neutral[500],
  display: 'flex',
  flexWrap: 'nowrap',
  fontSize: getScaledPx(10),
  gap: getScaledPx(14),
  minHeight: getScaledPx(31),
  overflow: 'hidden',
  padding: `${getScaledPx(8)} ${getScaledPx(16)}`,
  '@media (max-width: 720px)': {
    gap: getScaledPx(10),
    padding: `${getScaledPx(8)} ${getScaledPx(12)}`,
    '& > :nth-of-type(3), & > :nth-of-type(4)': {
      display: 'none',
    },
  },
}));

export const FooterContext = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: getScaledPx(4),
  minWidth: 0,
  '& svg': {
    color: theme.palette.neutral[500],
    fontSize: getScaledPx(11),
  },
  '@media (max-width: 720px)': {
    display: 'none',
  },
}));

export const FooterTrailing = styled(Box)({
  alignItems: 'center',
  display: 'flex',
  gap: getScaledPx(8),
  marginLeft: 'auto',
  minWidth: 0,
});

export const FooterClearHistory = styled(ButtonBase)(({ theme }) => ({
  borderRadius: getScaledPx(theme.shape.radius.xs),
  color: theme.palette.neutral[500],
  flex: '0 0 auto',
  fontSize: getScaledPx(10),
  lineHeight: getScaledPx(14),
  padding: `${getScaledPx(2)} ${getScaledPx(4)}`,
  transition: theme.transitions.create(['background-color', 'color'], { duration: 120 }),
  '&:hover': {
    backgroundColor: theme.palette.neutral[100],
    color: theme.palette.neutral[700],
  },
  '&:focus-visible': {
    boxShadow: `0 0 0 ${getScaledPx(2)} ${alpha(theme.palette.brand[500], 0.16)}`,
    color: theme.palette.neutral[700],
    outline: 'none',
  },
}));

export const FooterContextName = styled('span', {
  shouldForwardProp: shouldForwardToneProp,
})<ToneStyleProps>(({ theme, tone }) => ({
  color: getToneColors(tone, theme).foreground,
  fontWeight: theme.typography.fontWeightMedium,
  maxWidth: getScaledPx(130),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const KeyHint = styled(Box)({
  alignItems: 'center',
  display: 'flex',
  gap: getScaledPx(6),
  whiteSpace: 'nowrap',
});

export const KeyCap = styled('kbd')(({ theme }) => ({
  backgroundColor: theme.palette.neutral[100],
  border: `${getScaledPx(1)} solid ${theme.palette.neutral[300]}`,
  borderRadius: getScaledPx(theme.shape.radius.xs),
  color: theme.palette.neutral[700],
  fontFamily: theme.typography.fontFamily,
  fontSize: getScaledPx(9),
  lineHeight: 1,
  minWidth: getScaledPx(17),
  padding: `${getScaledPx(1)} ${getScaledPx(5)}`,
  textAlign: 'center',
  transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow', 'color', 'transform'], {
    duration: 90,
  }),
  '&[data-pressed="true"]': {
    backgroundColor: alpha(theme.palette.brand[500], 0.12),
    borderColor: theme.palette.brand[500],
    boxShadow: `0 0 0 ${getScaledPx(2)} ${alpha(theme.palette.brand[500], 0.1)}`,
    color: theme.palette.brand[500],
    transform: `translateY(${getScaledPx(1)})`,
  },
}));
